// src/pages/ProPetsPage.js

import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import API from '../api/client';
import ProPetCard from '../components/ProPetCard';
import FilterHeaderPro from '../components/FilterHeaderPro'; // Ahora lo usamos aquí
import ModalSignIn from '../components/ModalSignIn';
import { AuthContext }  from '../context/AuthContext';
import { TabContext }   from '../context/TabContext';
import { ModalContext } from '../context/ModalContext';
import './LikesPage.css';

export default function ProPetsPage() {
  // ─── Contextos e ID de profesional ─────────────────────────────
  const { user, isAuthenticated } = useContext(AuthContext);
  const professionalId = user?.professional_id;
  const { activeTab } = useContext(TabContext);
  const { openModal } = useContext(ModalContext);

  // ─── Outlet context para filtros Pro ─────────────────────────────
  const {
    showFilterHeader,
    hideFilterHeader
  } = useOutletContext();

  // ─── Estados de lista, paginación y loading ─────────────────────
  const [pets, setPets]       = useState([]);
  const [page, setPage]       = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  // ─── Estados para filtros Pro (especie + nombre) ────────────────
  const [proFilters, setProFilters] = useState({
    species: null,
    name:    ''
  });

  // ─── 1️⃣ Construye params base ────────────────────────────────────
  const buildParams = useCallback(pageNumber => ({
    page: pageNumber,
    _: Date.now()
  }), []);

  // ─── 2️⃣ Función fetchPets con filtros ────────────────────────────
  const fetchPets = useCallback(
    async (pageNumber, filtersArg = null) => {
      if (!professionalId) return;
      setLoading(true);
      try {
        const filtersToUse = filtersArg || proFilters;
        const params = buildParams(pageNumber);
        if (filtersToUse.species) params.species = filtersToUse.species;
        if (filtersToUse.name)    params.name    = filtersToUse.name;

        const token = localStorage.getItem('accessToken');
        const config = {
          params,
          ...(token && { headers: { Authorization: `Bearer ${token}` } })
        };

        const resp = await API.get(
          `/profesionales/${professionalId}/pets/`,
          config
        );
        const data = resp.data;
        const results = Array.isArray(data.results) ? data.results : [];

        setPets(prev => {
          const combined = pageNumber === 1 ? results : [...prev, ...results];
          return Array.from(
            combined.reduce((map, pet) => {
              if (!map.has(pet.id)) map.set(pet.id, pet);
              return map;
            }, new Map()).values()
          );
        });

        setHasMore(data.next != null);
        setPage(pageNumber);
      } catch (err) {
        console.error('[ProPetsPage] fetchPets error:', err);
      } finally {
        setLoading(false);
      }
    },
    [professionalId, buildParams, proFilters]
  );

  // ─── 3️⃣ Cargar inicial ──────────────────────────────────────────
  useEffect(() => {
    if (isAuthenticated && professionalId) {
      setPets([]);
      setPage(1);
      setHasMore(true);
      fetchPets(1);
    }
  }, [isAuthenticated, professionalId, fetchPets]);

  // ─── 4️⃣ Infinite scroll ─────────────────────────────────────────
  useEffect(() => {
    const container = document.querySelector('.pro-container');
    if (!container) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - 400 &&
          hasMore && !loading) {
        fetchPets(page + 1);
      }
    };
    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [page, hasMore, loading, fetchPets]);

  // ─── 5️⃣ Login/Register ──────────────────────────────────────────
  const handleOpenLogin = () =>
    openModal(<ModalSignIn initialTab="login" />, { tab: activeTab });
  const handleOpenRegister = () =>
    openModal(<ModalSignIn initialTab="register" />, { tab: activeTab });

  // ─── 6️⃣ Renderizado final con FilterHeaderPro inline ──────────
  return (
    <div className="likes-page-outer">
      {/* Mostrar FilterHeaderPro cuando lo soliciten */}
      {showFilterHeader && (
        <FilterHeaderPro
          initialFilters={proFilters}
          onApply={filters => {
            // Aplicar los filtros y recargar desde página 1
            setProFilters(filters);
            setPets([]);
            setPage(1);
            setHasMore(true);
            fetchPets(1, filters);
          }}
          onHide={hideFilterHeader}
        />
      )}

      <div className="likes-page-inner pro-container">
        {!isAuthenticated ? (
          /* … estados de not-auth, permisos, loading, etc. … */
          <div className="not-authenticated">
            <p>Para ver tus mascotas publicadas debes iniciar sesión.</p>
            <button className="btn" onClick={handleOpenLogin}>Iniciar sesión</button>
            <span className="separator">o</span>
            <button className="btn" onClick={handleOpenRegister}>Crear cuenta</button>
          </div>
        ) : !professionalId ? (
          <p>No tienes permisos para ver esta sección.</p>
        ) : loading && pets.length === 0 ? (
          <p>Cargando tus mascotas…</p>
        ) : pets.length === 0 && !loading ? (
          <p>No has publicado aún ninguna mascota.</p>
        ) : (
          <>
            {pets.map(pet => (
              <ProPetCard key={pet.id} pet={pet} />
            ))}
            {loading && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <p>Cargando más mascotas…</p>
              </div>
            )}
            {!hasMore && !loading && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <p>No hay más mascotas para mostrar.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
